import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { AppIcon } from './ui/icon'
import { useExpenses } from '../hooks/useQueries'
import { useFormatters } from '../lib/format'
import { Card, CardContent, CardHeader, CardTitle } from './ui/card'
import { SECTION_ICONS } from '../lib/section-icons'

interface Props {
  org: string
  interventionUid: string
}

/**
 * "Related expenses" section on the intervention detail page. Mirrors
 * `ContractRelatedExpenses` — lists the expenses linked to this intervention
 * (filtered via the `intervention_uid` query param). Renders nothing when
 * there are no related expenses.
 */
export function InterventionRelatedExpenses({ org, interventionUid }: Props) {
  const { t } = useTranslation('expenses')
  const { formatMoney } = useFormatters()

  const { data: expenses = [], isLoading } = useExpenses(org, {
    interventionUid,
    limit: 100,
    enabled: !!interventionUid,
  })

  // Amounts are stored in integer cents — divide by 100 for display.
  const formatAmount = (cents: number | null | undefined, currency?: string | null) => {
    if (cents == null) return '—'
    return formatMoney(cents, currency)
  }

  if (isLoading || expenses.length === 0) return null

  return (
    <Card className="mt-6" data-testid="intervention-related-expenses">
      <CardHeader>
        <CardTitle className="text-sm flex items-center gap-2">
          <AppIcon path={SECTION_ICONS.expenses} size="sm" />
          {t('related_intervention.title')}
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          {t(`related_intervention.summary_${expenses.length === 1 ? 'one' : 'other'}` as any, { count: expenses.length })}
        </p>
      </CardHeader>

      <CardContent className="space-y-1">
        {expenses.map((expense) => (
          <div
            key={expense.uid}
            className="flex items-center gap-2 px-3 py-1.5 border-b last:border-b-0 border-border/50"
            data-testid={`intervention-related-expense-row-${expense.uid}`}
          >
            <Link
              to="/$org/expenses/$expenseUid"
              params={{ org, expenseUid: expense.uid! }}
              className="text-sm font-medium text-foreground hover:underline flex-shrink min-w-0 truncate"
              data-testid={`intervention-related-expense-link-${expense.uid}`}
            >
              {expense.label || expense.uid}
            </Link>
            <div className="flex-1" />
            <span className="text-sm font-medium text-foreground flex-shrink-0">
              {formatAmount(expense.amount, expense.currency)}
            </span>
          </div>
        ))}
      </CardContent>
    </Card>
  )
}
